import type { ReactNode } from 'react';
import { Header, type EyebrowTone, type TitleAccent } from './_shared';

export type DiagramNode = {
  /** Unique id, referenced by connections. */
  id: string;
  label: ReactNode;
  /** Optional leading icon (React node). */
  icon?: ReactNode;
  /** Center X as a percentage of the board (0–100). */
  x: number;
  /** Center Y as a percentage of the board (0–100). */
  y: number;
};

export type DiagramConnection = {
  /** Source node id. */
  from: string;
  /** Target node id. */
  to: string;
};

export type DiagramAnnotation = {
  text: ReactNode;
  /** Center X as a percentage of the board (0–100). */
  x: number;
  /** Center Y as a percentage of the board (0–100). */
  y: number;
};

export type DiagramSlideProps = {
  eyebrow: ReactNode;
  eyebrowTone?: EyebrowTone;
  title: ReactNode;
  titleAccent?: TitleAccent;
  sub?: ReactNode;
  nodes: readonly DiagramNode[];
  connections?: readonly DiagramConnection[];
  annotations?: readonly DiagramAnnotation[];
  /** 'straight' (default) or 'elbow' (orthogonal, single bend). */
  connectorStyle?: 'straight' | 'elbow';
  /** Draw arrowheads at the target end. */
  directed?: boolean;
};

export function DiagramSlide({
  eyebrow,
  eyebrowTone,
  title,
  titleAccent,
  sub,
  nodes,
  connections = [],
  annotations = [],
  connectorStyle = 'straight',
  directed = false,
}: DiagramSlideProps) {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  return (
    <div className="slide-body">
      <Header
        eyebrow={eyebrow}
        eyebrowTone={eyebrowTone}
        title={title}
        titleAccent={titleAccent}
        sub={sub}
      />
      <div className="sn-work">
        <div className="sn-diagram">
          <svg
            className="sn-diagram-links"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            {directed && (
              <defs>
                <marker id="sn-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                  <path d="M0 0 L10 5 L0 10 z" fill="var(--sn-teal-3)" />
                </marker>
              </defs>
            )}
            {connections.map((c, i) => {
              const a = byId.get(c.from);
              const b = byId.get(c.to);
              if (!a || !b) return null;
              const marker = directed ? 'url(#sn-arrow)' : undefined;
              if (connectorStyle === 'elbow') {
                const midX = (a.x + b.x) / 2;
                return (
                  <path
                    key={i}
                    d={`M ${a.x} ${a.y} H ${midX} V ${b.y} H ${b.x}`}
                    fill="none"
                    vectorEffect="non-scaling-stroke"
                    markerEnd={marker}
                  />
                );
              }
              return (
                <line
                  key={i}
                  x1={a.x}
                  y1={a.y}
                  x2={b.x}
                  y2={b.y}
                  vectorEffect="non-scaling-stroke"
                  markerEnd={marker}
                />
              );
            })}
          </svg>
          {annotations.map((an, i) => (
            <p
              key={i}
              className="sn-diagram-note"
              style={{ left: `${an.x}%`, top: `${an.y}%` }}
            >
              {an.text}
            </p>
          ))}
          {nodes.map((n) => (
            <div
              key={n.id}
              className="sn-diagram-node"
              style={{ left: `${n.x}%`, top: `${n.y}%` }}
            >
              {n.icon && <span className="sn-diagram-node-icon">{n.icon}</span>}
              <span className="sn-diagram-node-label">{n.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
